import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, PanelLeftClose, PanelLeftOpen, Sparkles } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useLiveStatus } from '../../contexts/LiveStatusContext'
import {
  HELP_CENTRE_ITEM,
  HELP_SUPPORT_ITEM,
  NAV_SECTIONS,
  navItemVisible,
  navSectionVisible,
  findExpandedParentKeys,
  pathInSubtree,
  pathMatchesItem,
  type NavItem,
} from './navConfig'
import { api } from '../../lib/api'
import { isPendingOrderNotificationId } from '../../lib/smartNotifications'
import {
  adminSupportApi,
  adminSupportKeys,
  supportApi,
  supportKeys,
  supportQueryRetry,
  supportRefetchInterval,
} from '../../lib/support'
import { useSupportEntry } from '../support/useSupportEntry'
import { useHelpArticles } from '../help/useHelp'
import { ASSISTANT_SHORTCUT_LABEL, assistantShortcutHint } from '../../lib/assistantShortcut'

/** The AI entry at the foot of the menu (desktop, for users with AI). */
export interface SidebarAssistant {
  /** The AI panel is on screen. */
  open: boolean
  onToggle: () => void
}

interface SidebarProps {
  open: boolean
  setOpen: (open: boolean) => void
  /** Desktop, AI open: the 64 px icon column instead of the full menu. */
  compact?: boolean
  /** Icon column: "Show full menu" (beside the AI). */
  onShowFullMenu?: () => void
  /**
   * Full menu: what its own hide button does. Left out, it hides the whole
   * menu; beside the open AI it shrinks the menu back to icons.
   */
  onCollapse?: () => void
  collapseLabel?: string
  assistant?: SidebarAssistant
}

function closeDrawerIfMobile(setOpen: (open: boolean) => void) {
  if (window.innerWidth < 1024) {
    setOpen(false)
  }
}

function NavItemLink({
  item,
  setOpen,
  badges,
  depth,
  expandedKeys,
  onToggleExpand,
}: {
  item: NavItem
  setOpen: (open: boolean) => void
  badges: Record<string, number>
  depth: number
  expandedKeys: Set<string>
  onToggleExpand: (key: string) => void
}) {
  const { hasPermission, hasNavPermission, isPlatformAdmin, activePersona, modules } = useAuth()
  const { pathname } = useLocation()
  const { label, to, icon: Icon, end, subItems, badgeKey } = item
  const badgeCount = badgeKey ? badges[badgeKey] ?? 0 : 0
  const visibility = { isPlatformAdmin, activePersona, hasNavPermission, modules }

  if (!navItemVisible(item, hasPermission, visibility)) return null

  const visibleSubItems =
    subItems?.filter((sub) => navItemVisible(sub, hasPermission, visibility)) ?? []
  const hasChildren = visibleSubItems.length > 0
  const isExpanded = hasChildren && expandedKeys.has(to)
  const linkClass = depth === 0 ? 'nav-item' : 'nav-sub-item'
  const iconSize = depth === 0 ? 18 : 14

  return (
    <div className="space-y-1">
      <div className="flex items-stretch gap-0.5">
        <NavLink
          to={to}
          end={end}
          onClick={() => {
            if (hasChildren) {
              if (!isExpanded) onToggleExpand(to)
            } else {
              closeDrawerIfMobile(setOpen)
            }
          }}
          title={label}
          className={({ isActive }) => {
            // Parents with children: highlight only when this exact route is active
            // and no more-specific child owns the path (avoids double-active with Board).
            let active = isActive
            if (hasChildren) {
              const childOwnsPath = visibleSubItems.some((sub) => pathMatchesItem(pathname, sub))
              active = pathMatchesItem(pathname, item) && !childOwnsPath
            }
            return `${linkClass} flex-1 ${active ? 'active' : ''}`
          }}
        >
          <Icon size={iconSize} className="shrink-0" />
          <span className="flex-1">{label}</span>
          {badgeCount > 0 && (
            <span className="ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-amber-950 text-[10px] font-bold flex items-center justify-center">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </NavLink>
        {hasChildren && (
          <button
            type="button"
            aria-label={isExpanded ? `Collapse ${label}` : `Expand ${label}`}
            aria-expanded={isExpanded}
            onClick={() => onToggleExpand(to)}
            className="shrink-0 px-1.5 rounded-lg text-sidebar-item/70 hover:text-sidebar-item hover:bg-sidebar-active-bg/50 transition-colors"
          >
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
            />
          </button>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="pl-6 space-y-1 mt-1">
          {visibleSubItems.map((sub) => (
            <NavItemLink
              key={`${sub.to}:${sub.label}`}
              item={sub}
              setOpen={setOpen}
              badges={badges}
              depth={depth + 1}
              expandedKeys={expandedKeys}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** One top-level entry in the icon column: the icon, its name as a tooltip and
 *  accessible name, a dot for a live badge. A group links to its own route and
 *  is marked while any of its pages is open. */
function IconNavLink({ item, badges }: { item: NavItem; badges: Record<string, number> }) {
  const { hasPermission, hasNavPermission, isPlatformAdmin, activePersona, modules } = useAuth()
  const { pathname } = useLocation()
  const visibility = { isPlatformAdmin, activePersona, hasNavPermission, modules }

  if (!navItemVisible(item, hasPermission, visibility)) return null

  const { label, to, icon: Icon, badgeKey } = item
  const visibleSubItems =
    item.subItems?.filter((sub) => navItemVisible(sub, hasPermission, visibility)) ?? []
  const active =
    pathMatchesItem(pathname, item) || visibleSubItems.some((sub) => pathInSubtree(pathname, sub))
  const badgeCount = badgeKey ? badges[badgeKey] ?? 0 : 0
  const name = badgeCount > 0 ? `${label} (${badgeCount > 99 ? '99+' : badgeCount})` : label

  return (
    <Link
      to={to}
      aria-label={name}
      title={name}
      aria-current={active ? 'page' : undefined}
      className={`nav-icon-item ${active ? 'active' : ''}`}
    >
      <Icon size={18} aria-hidden="true" />
      {badgeCount > 0 ? (
        <span
          aria-hidden="true"
          className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-sidebar"
        />
      ) : null}
    </Link>
  )
}

/** Ask AI at the foot of the full menu; the filled sparkle in the icon column. */
function AssistantFoot({ assistant, compact }: { assistant: SidebarAssistant; compact: boolean }) {
  const label = assistant.open ? 'Hide AI' : 'Ask AI'
  const title = `${label} (${ASSISTANT_SHORTCUT_LABEL})`
  if (compact) {
    return (
      <button
        type="button"
        data-testid="sidebar-assistant-toggle"
        onClick={assistant.onToggle}
        aria-label={label}
        title={title}
        aria-keyshortcuts="Control+. Meta+."
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors ${
          assistant.open
            ? 'border-accent bg-accent text-white hover:opacity-90'
            : 'border-accent/50 bg-accent/10 text-accent hover:bg-accent/20'
        }`}
      >
        <Sparkles size={18} aria-hidden="true" />
      </button>
    )
  }
  return (
    <button
      type="button"
      data-testid="sidebar-assistant-toggle"
      onClick={assistant.onToggle}
      aria-label={label}
      title={title}
      aria-keyshortcuts="Control+. Meta+."
      className={`w-full flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
        assistant.open
          ? 'border-accent bg-accent text-white hover:opacity-90'
          : 'border-accent/50 bg-accent/10 text-white hover:bg-accent/20'
      }`}
    >
      <Sparkles size={17} aria-hidden="true" className={assistant.open ? 'text-white' : 'text-accent'} />
      <span className="flex-1 text-left">{label}</span>
      <kbd
        aria-hidden="true"
        className={`font-mono text-[10px] rounded px-1 py-0.5 border ${
          assistant.open ? 'border-white/40 text-white/80' : 'border-white/15 text-white/60'
        }`}
      >
        {assistantShortcutHint()}
      </kbd>
    </button>
  )
}

export default function Sidebar({
  open,
  setOpen,
  compact = false,
  onShowFullMenu,
  onCollapse,
  collapseLabel = 'Collapse left menu',
  assistant,
}: SidebarProps) {
  const { hasPermission, hasNavPermission, isPlatformAdmin, activePersona, modules } = useAuth()
  const { pathname } = useLocation()
  const visibility = { isPlatformAdmin, activePersona, hasNavPermission, modules }

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set())
  const [manualKeys, setManualKeys] = useState<Set<string> | null>(null)

  // Auto-expand the active parent group; clear manual overrides on navigation.
  useEffect(() => {
    const auto = findExpandedParentKeys(NAV_SECTIONS, pathname, hasPermission, visibility)
    setExpandedKeys(new Set(auto))
    setManualKeys(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visibility object is recreated each render
  }, [pathname, hasPermission, hasNavPermission, isPlatformAdmin, activePersona, modules])

  const effectiveExpanded = manualKeys ?? expandedKeys

  const onToggleExpand = (key: string) => {
    setManualKeys((prev) => {
      const base = new Set(prev ?? expandedKeys)
      if (base.has(key)) base.delete(key)
      else base.add(key)
      return base
    })
  }

  const { data: adminStats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const res = await api.get<{ pendingApplications?: number }>('/admin/stats')
      return res.data
    },
    enabled: isPlatformAdmin && hasPermission('admin:companies:read') && activePersona === 'Admin',
    staleTime: 60_000,
  })

  const merchantOrdersPersona =
    activePersona === 'Merchant' || activePersona === 'Manager' || activePersona === 'Worker'
  // The live order stream refreshes the badge; poll only while it is down.
  const { connected: liveConnected } = useLiveStatus()

  const { data: notifications } = useQuery({
    queryKey: ['notifications', 'merchant'],
    queryFn: async () => {
      const res = await api.get<{ id: string }[]>('/notifications')
      return Array.isArray(res.data) ? res.data : []
    },
    enabled: merchantOrdersPersona && hasPermission('orders:read'),
    staleTime: 30_000,
    refetchInterval: liveConnected ? false : 30_000,
  })

  const dismissedIds = (() => {
    try {
      const saved = localStorage.getItem('ch_dismissed_notifications')
      const parsed = saved ? JSON.parse(saved) : []
      return Array.isArray(parsed) ? (parsed as string[]) : []
    } catch {
      return [] as string[]
    }
  })()

  const orderActions =
    notifications?.filter(
      (n) => isPendingOrderNotificationId(n.id) && !dismissedIds.includes(n.id),
    ).length ?? 0

  // Platform › Support's badge: the requests waiting for a reply. The live
  // stream's `support` frames refresh it; it polls only while the stream is down.
  const { data: supportSummary } = useQuery({
    queryKey: adminSupportKeys.summary,
    queryFn: adminSupportApi.summary,
    enabled: isPlatformAdmin && activePersona === 'Admin' && hasPermission('admin:support:read'),
    staleTime: 30_000,
    refetchInterval: supportRefetchInterval(liveConnected),
    retry: supportQueryRetry,
  })

  // Help & support for shops, couriers and partner desks: only once the server
  // answers GET /api/support/options for the persona's side.
  const supportEntry = useSupportEntry()
  const { data: supportUnread } = useQuery({
    queryKey: supportKeys.unread,
    queryFn: supportApi.unreadCount,
    enabled: supportEntry.available,
    staleTime: 30_000,
    refetchInterval: supportRefetchInterval(liveConnected),
    retry: supportQueryRetry,
  })

  // The foot link opens the help centre once the server has it; without help
  // (404) it goes to the support page as before, and without either it hides.
  // The platform admin gets none: they reach /help from "?" or the address bar.
  const help = useHelpArticles()
  const helpItem: NavItem | null =
    activePersona === 'Admin'
      ? null
      : help.available
        ? HELP_CENTRE_ITEM
        : supportEntry.available
          ? HELP_SUPPORT_ITEM
          : null

  const count = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0)
  const badges: Record<string, number> = {
    pendingApplications: adminStats?.pendingApplications ?? 0,
    orderActions,
    supportNeedsReply: count(supportSummary?.needsReply),
    supportUnread: count(supportUnread?.count),
  }

  const visibleSections = NAV_SECTIONS.flatMap((section) => {
    if (!navSectionVisible(section, hasPermission, visibility)) return []
    const items = section.items.filter((item) => navItemVisible(item, hasPermission, visibility))
    return items.length > 0 ? [{ section, items }] : []
  })

  const logo = (
    <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-accent to-accent-strong flex items-center justify-center shadow-md shrink-0">
      <span className="text-white font-bold text-sm tracking-tighter">CH</span>
    </div>
  )

  const frameClass =
    'fixed lg:static inset-y-0 left-0 shrink-0 bg-sidebar border-r border-sidebar-border flex flex-col overflow-hidden transition-all duration-300 z-50 shadow-lg lg:shadow-none pb-[env(safe-area-inset-bottom)]'

  // Desktop, AI open: the icon column (only Shell's desktop layout asks for it).
  if (compact && open) {
    return (
      <aside className={`${frameClass} w-16 pt-6 items-center`} data-testid="sidebar-icon-rail">
        <div className="flex flex-col items-center gap-3 mb-4 shrink-0">
          {logo}
          <button
            type="button"
            onClick={onShowFullMenu}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Show full menu"
            title="Show full menu"
          >
            <PanelLeftOpen size={18} aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="Main menu" className="flex-1 min-h-0 w-full overflow-y-auto px-2 pb-4">
          <div className="flex flex-col items-center gap-1">
            {visibleSections.map(({ section, items }, index) => (
              <div
                key={section.label || 'main'}
                className={`flex flex-col items-center gap-1 w-full ${
                  index > 0 ? 'mt-2 pt-2 border-t border-sidebar-border' : ''
                }`}
              >
                {items.map((item) => (
                  <IconNavLink key={`${item.to}:${item.label}`} item={item} badges={badges} />
                ))}
              </div>
            ))}
            {helpItem ? (
              <div className="mt-2 pt-2 border-t border-sidebar-border flex flex-col items-center w-full">
                <IconNavLink item={helpItem} badges={badges} />
              </div>
            ) : null}
          </div>
        </nav>
        {assistant ? (
          <div className="shrink-0 py-4 border-t border-sidebar-border w-full flex justify-center">
            <AssistantFoot assistant={assistant} compact />
          </div>
        ) : null}
      </aside>
    )
  }

  return (
    <aside
      className={`${frameClass} ${
        open
          ? 'w-[268px] translate-x-0 lg:w-60 pt-6'
          : '-translate-x-full lg:translate-x-0 lg:w-0 lg:border-r-0'
      }`}
    >
      <div className="px-4 mb-8 flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {logo}
          <span className="font-display font-bold text-lg tracking-tight text-white truncate">
            Comfort<span className="text-accent">Hub</span>
          </span>
        </div>
        <button
          type="button"
          onClick={() => (onCollapse ? onCollapse() : setOpen(false))}
          className="hidden lg:inline-flex p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors shrink-0"
          aria-label={collapseLabel}
          title={collapseLabel}
        >
          <PanelLeftClose size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pb-6">
        <nav className="px-2">
          {visibleSections.map(({ section, items }) => (
            <div key={section.label || 'main'} className="mb-1">
              {section.label ? (
                <p className="nav-group-label px-3 pb-1 pt-4">{section.label}</p>
              ) : null}
              <div className="space-y-1">
                {items.map((item) => (
                  <NavItemLink
                    key={`${item.to}:${item.label}`}
                    item={item}
                    setOpen={setOpen}
                    badges={badges}
                    depth={0}
                    expandedKeys={effectiveExpanded}
                    onToggleExpand={onToggleExpand}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {helpItem ? (
          <div className="mt-4 border-t border-sidebar-border px-2 pt-4" data-testid="sidebar-help">
            <NavItemLink
              item={helpItem}
              setOpen={setOpen}
              badges={badges}
              depth={0}
              expandedKeys={effectiveExpanded}
              onToggleExpand={onToggleExpand}
            />
          </div>
        ) : null}
      </div>

      {assistant ? (
        <div className="shrink-0 px-3 py-4 border-t border-sidebar-border">
          <AssistantFoot assistant={assistant} compact={false} />
        </div>
      ) : null}
    </aside>
  )
}
